/**
 * The names an audio-out page shows for what it found and what it will save, and the routing of a
 * dropped file. Pure, so the tests read it directly.
 *
 * Codec and file-type names are format names, the same in every language (server/app/i18n_strings.py
 * keeps SRT and UTF-8 as they are for the same reason), so they live here rather than in the
 * template. Every sentence around them is the template's.
 */

import { classify, extensionOf } from '../../../base/src/lib/dropzone.ts';
import { mediaFormat, type UsageFormat } from '../../../base/src/lib/usage.ts';

const CODEC_NAMES: ReadonlyMap<string, string> = new Map([
  ['aac', 'AAC'],
  ['mp3', 'MP3'],
  ['opus', 'Opus'],
  ['vorbis', 'Vorbis'],
  ['flac', 'FLAC'],
  ['ac3', 'AC-3'],
  ['eac3', 'E-AC-3'],
  ['dts', 'DTS'],
  ['ulaw', 'μ-law'],
  ['alaw', 'A-law'],
]);

/** `aac` → `AAC`, `eac3` → `E-AC-3`, any `pcm-*` → `PCM`. An unknown name is shown upper-cased. */
export function codecName(codec: string): string {
  if (codec.startsWith('pcm-')) return 'PCM';
  return CODEC_NAMES.get(codec) ?? codec.toUpperCase();
}

/** The file type as the page names it: `m4a` → `M4A`. */
export function typeName(container: string): string {
  return container.toUpperCase();
}

/**
 * The format a saved file is counted under (lib/usage.ts): M4A, MP3 and WAV by name, the other
 * types as `audio`. Only a value from the counter's list ever leaves here.
 */
export function savedFormat(container: string): UsageFormat {
  return mediaFormat(container, 'audio');
}

/**
 * The format a dropped file is counted under: the video's container, `video` for one in a format
 * nothing here opens (WMV, FLV, MPG…), or `audio`/`unknown`.
 */
export function droppedFormat(file: File): UsageFormat {
  const kind = classify(file);
  if (kind === 'container') return mediaFormat(extensionOf(file.name), 'video');
  if (kind === 'other-video') return 'video';
  if (kind === 'audio') return mediaFormat(extensionOf(file.name), 'audio');
  return kind === 'archive' ? 'zip' : 'unknown';
}

/**
 * What a dropped file does on the page. A video starts the job. An audio file starts it too when
 * the page re-encodes (`acceptsAudio`), since an M4A or a WAV can become an MP3; on the copy page
 * it is already what the page would hand back, and the page says so. A video in a format nothing
 * here opens (WMV, FLV, MPG…) gets B5_VIDEO_FORMAT's words. Anything else is the wrong file.
 */
export type DropRoute = 'start' | 'audio-file' | 'video-format' | 'wrong-file';

export function routeDrop(file: File, acceptsAudio: boolean): DropRoute {
  const kind = classify(file);
  if (kind === 'container') return 'start';
  if (kind === 'other-video') return 'video-format';
  if (kind === 'audio') return acceptsAudio ? 'start' : 'audio-file';
  return 'wrong-file';
}
